import { useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';

import { NotLoggedInBadge } from '@/components/NotLoggedInBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { statusVariant } from '@/components/UserEditTeamSection';
import { cn } from '@/lib/utils';

import { isWithinDays, relativeDays } from './format';
import { NewTeamDialog } from './NewTeamDialog';
import { PAGE_SIZE, useTeamsList } from './useAccounts';

export function TeamsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isNewTeamOpen, setIsNewTeamOpen] = useState(false);
  const { data, isLoading, error } = useTeamsList(page, search);
  const totalPages = Math.ceil((data?.total_count ?? 0) / PAGE_SIZE);

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <CardTitle>Teams</CardTitle>
            <CardDescription>
              Every team on Littlebird{data ? ` (${data.total_count})` : ''}
            </CardDescription>
          </div>
          <Button onClick={() => setIsNewTeamOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            New team
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <form
            className="relative min-w-64 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput.trim());
              setPage(1);
            }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by team name or owner email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </form>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Team</TableHead>
              <TableHead>Owner email</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Seats</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Trial ends</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">
                  Loading teams...
                </TableCell>
              </TableRow>
            )}
            {data?.teams.map((team) => {
              const status = statusVariant(team.subscription_status);
              const trialEndMs = team.trial_end ? team.trial_end * 1000 : null;
              return (
                <TableRow
                  key={team.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/teams/$teamId',
                      params: { teamId: team.id },
                    })
                  }
                >
                  <TableCell className="font-medium">{team.name}</TableCell>
                  <TableCell>
                    {team.owner_email ?? '-'}{' '}
                    {team.owner_email && !team.owner_has_logged_in && (
                      <NotLoggedInBadge />
                    )}
                  </TableCell>
                  <TableCell className="capitalize">
                    {team.tier ?? '-'}
                  </TableCell>
                  <TableCell>
                    {team.active_seats} / {team.total_seats}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={status.className}>
                      {status.label}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={cn(
                      trialEndMs &&
                        isWithinDays(trialEndMs, 7) &&
                        'font-medium text-amber-600'
                    )}
                  >
                    {trialEndMs ? relativeDays(trialEndMs) : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
            {data?.teams.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">
                  No teams match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span>
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
      {isNewTeamOpen && (
        <NewTeamDialog onClose={() => setIsNewTeamOpen(false)} />
      )}
    </Card>
  );
}
